import { useEffect, useState, useMemo, useRef } from "react";
import { MultiSelect } from 'primereact/multiselect';
import { useGetUsersQuery } from "../services/usersApi";
import { useGetInstitutionsQuery } from "../services/institutionsApi";
import { renderAvatar } from '../utils/avatar';
import { useChannelQuery } from "../services/websocketApi.ts";
import { FloatLabel } from "primereact/floatlabel";
import { getBranchOptions } from "./BranchDropdown";

// Helper to get initials for search (1st letter + 2 letters of last name)
const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  return parts.map((p, id) => p.substring(0, id + 1)).join("");
};

export default function UserDropdown({ selectedUser, onUserSelect, width = "100%", className, floating = true, label = "Wykonawca", userTypes = null, disabled = false, showBranchColor = false, branchFilter = [], usersData = null, institutionsData = null }) {
  const hasProvidedUsers = Array.isArray(usersData);
  const hasProvidedInstitutions = Array.isArray(institutionsData);
  const users = useGetUsersQuery(undefined, { skip: hasProvidedUsers });
  const institutions = useGetInstitutionsQuery(undefined, { skip: hasProvidedInstitutions });
  const usersList = hasProvidedUsers ? usersData : (users.data || []);
  const institutionsList = hasProvidedInstitutions ? institutionsData : (institutions.data || []);
  const usersLoading = hasProvidedUsers ? false : users.isLoading;
  const institutionsLoading = hasProvidedInstitutions ? false : institutions.isLoading;
  const [filteredData, setFilteredData] = useState(null);
  const [displayData, setDisplayData] = useState(null);
  const [validatedSelection, setValidatedSelection] = useState([]);
  const lastUsersRefetchAtRef = useRef(0);
  const lastInstitutionsRefetchAtRef = useRef(0);
  const lastUserEventKeyRef = useRef(null);
  const lastInstitutionEventKeyRef = useRef(null);

  // Stabilize userTypes reference to prevent infinite loops
  const userTypesKey = useMemo(() => 
    userTypes ? JSON.stringify(userTypes) : null, 
    [userTypes ? JSON.stringify(userTypes) : null]
  );

  const branchFilterKey = useMemo(
    () => (Array.isArray(branchFilter) ? JSON.stringify(branchFilter) : null),
    [Array.isArray(branchFilter) ? JSON.stringify(branchFilter) : null]
  );

  const { data: userEvents } = useChannelQuery('users');

  const branchColorByValue = useMemo(() => {
    const map = new Map();
    getBranchOptions().forEach((branch) => map.set(branch.value, branch.code));
    return map;
  }, []);

  const getUserBranchColor = (user) => {
    if (!showBranchColor) return null;
    if (!user || !user.branch) return null;
    return branchColorByValue.get(user.branch) || null;
  };

  useEffect(() => {
    if (usersList) {
      lastUsersRefetchAtRef.current = Date.now();
    }
  }, [usersList]);

  useEffect(() => {
    if (institutionsList) {
      lastInstitutionsRefetchAtRef.current = Date.now();
    }
  }, [institutionsList]);

  useEffect(() => {
    if (hasProvidedUsers) return;
    const messages = userEvents?.messages || [];
    const latest = messages[messages.length - 1];
    if (!latest) return;

    const latestKey = latest._id || `${latest.id || ''}-${latest.author || ''}-${latest.message || ''}`;
    if (!latestKey || lastUserEventKeyRef.current === latestKey) return;
    lastUserEventKeyRef.current = latestKey;

    const now = Date.now();
    if (now - lastUsersRefetchAtRef.current < 1200) return;

    lastUsersRefetchAtRef.current = now;
    if (users) {
      users.refetch();
    }
  }, [userEvents, hasProvidedUsers, users]);

  const { data: institutionEvents } = useChannelQuery('institutions');

  useEffect(() => {
    if (hasProvidedInstitutions) return;
    const messages = institutionEvents?.messages || [];
    const latest = messages[messages.length - 1];
    if (!latest) return;

    const latestKey = latest._id || `${latest.id || ''}-${latest.author || ''}-${latest.message || ''}`;
    if (!latestKey || lastInstitutionEventKeyRef.current === latestKey) return;
    lastInstitutionEventKeyRef.current = latestKey;

    const now = Date.now();
    if (now - lastInstitutionsRefetchAtRef.current < 1200) return;

    lastInstitutionsRefetchAtRef.current = now;
    if (institutions) {
      institutions.refetch();
    }
  }, [institutionEvents, hasProvidedInstitutions, institutions]);

  // Build data array when users/institutions load
  useEffect(() => {
    if (!usersLoading && !institutionsLoading) {
      // Filter users by type if userTypes prop is provided
      let filteredUsers = usersList;
      if (userTypes && Array.isArray(userTypes) && userTypes.length > 0) {
        filteredUsers = usersList.filter(u => userTypes.includes(u.type));
      }

      // Filter users by selected branches if provided (used in TopMenu executor filter)
      if (Array.isArray(branchFilter) && branchFilter.length > 0) {
        filteredUsers = filteredUsers.filter(u => branchFilter.includes(u.branch));
      }
      
      // Add searchField to each user/institution containing name + initials for filtering
      const enrichedUsers = filteredUsers.map(u => ({
        ...u,
        searchField: `${u.name} ${getInitials(u.name)}`
      })).sort((a, b) => a.name.localeCompare(b.name, 'pl'));
      
      const enrichedInstitutions = institutionsList.map(i => ({
        ...i,
        searchField: `${i.name}`
      })).sort((a, b) => a.name.localeCompare(b.name, 'pl'));

      const data = [
        {
          label: "Pracownicy",
          code: "workers",
          items: enrichedUsers
        }
      ];
      
      // Only include institutions if userTypes filter is not set
      if (!userTypes || !Array.isArray(userTypes) || userTypes.length === 0) {
        data.push({
          label: "Instytucje",
          code: "institutions",
          items: enrichedInstitutions
        });
      }

      setDisplayData(data);
      setFilteredData(data);
    }
  }, [usersLoading, institutionsLoading, usersList, institutionsList, userTypesKey, branchFilterKey]);

  // Validate and filter selectedUser to remove non-existing users
  useEffect(() => {
    if (!usersLoading && !institutionsLoading && selectedUser) {
      const allValidIds = new Set([
        ...usersList.map(u => u._id),
        ...institutionsList.map(i => i._id)
      ]);
      
      const validSelection = Array.isArray(selectedUser) 
        ? selectedUser.filter(id => allValidIds.has(id))
        : allValidIds.has(selectedUser) ? selectedUser : null;
      
      setValidatedSelection(validSelection);
      
      // If some users were removed, notify parent component
      const selectedArray = Array.isArray(selectedUser) ? selectedUser : [selectedUser];
      const validArray = Array.isArray(validSelection) ? validSelection : validSelection ? [validSelection] : [];
      
      if (selectedArray.length !== validArray.length && onUserSelect) {
        // Silently update to remove missing users
        onUserSelect({ value: validSelection });
      }
    } else if (!selectedUser) {
      setValidatedSelection([]);
    }
  }, [usersLoading, institutionsLoading, usersList, institutionsList, selectedUser, onUserSelect]);


  const userOptionTemplate = (user) => (
    <div className="flex align-items-center gap-2">
      {renderAvatar(user)}
      {showBranchColor && (
        <span
          style={{
            width: '1.2rem',
            height: '0.7rem',
            border: '1px solid #bfbfbf',
            borderRadius: 2,
            backgroundColor: getUserBranchColor(user) || 'transparent',
            flexShrink: 0
          }}
          title={user?.branch || ''}
        />
      )}
      <div style={{ flex: 1 }}>
        <div>{user.name}</div>
        {user.email && <div style={{ fontSize: '0.85em', color: '#888' }}>{user.email}</div>}
      </div>
    </div>
  );

  const selectedUserTemplate = (user, props) => {
    if (user) {

      return (
        <div className="flex align-items-center gap-2">
          {renderAvatar(user)}
          {showBranchColor && (
            <span
              style={{
                width: '1.2rem',
                height: '0.7rem',
                border: '1px solid #bfbfbf',
                borderRadius: 2,
                backgroundColor: getUserBranchColor(user) || 'transparent',
                flexShrink: 0
              }}
              title={user?.branch || ''}
            />
          )}
          <div style={{ flex: 1 }}>
            <div>{user.name}</div>
            {user.email && <div style={{ fontSize: '0.85em', color: '#888' }}>{user.email}</div>}
          </div>
        </div>
      );
    }
    return <span>{props.placeholder}</span>;
  };

  const panelFooterTemplate = () => {
    const length = validatedSelection ? (Array.isArray(validatedSelection) ? validatedSelection.length : 1) : 0;

    const odmianaWykonawcow = n => n === 1 ? `wykonawca zaznaczony` : (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) ? `wykonawcy zaznaczeni` : `wykonawców zaznaczonych`;

    return (
      <div className="py-2 px-3">
        <b>{length}</b> {odmianaWykonawcow(length)}.
      </div>
    );
  };

  return (
    <>
      {!floating ? (
        <>
          <label htmlFor="userSelect">{label}</label>
          <MultiSelect
            value={validatedSelection}
            onChange={onUserSelect}
            options={filteredData ?? []}
            optionGroupLabel="label"
            optionGroupChildren="items"
            optionLabel="name"
            optionValue="_id"
            placeholder="Wybierz..."
            itemTemplate={userOptionTemplate}
            panelFooterTemplate={panelFooterTemplate}
            style={{ width: width ?? "100%" }}
            filter
            filterBy="searchField"
            className={className}
            loading={usersLoading || institutionsLoading}
            inputId="userSelect"
            disabled={disabled}
          />
        </>
      ) : (
        <FloatLabel className={className}>
          <MultiSelect
            value={validatedSelection}
            onChange={onUserSelect}
            options={filteredData ?? []}
            optionGroupLabel="label"
            optionGroupChildren="items"
            optionLabel="name"
            optionValue="_id"
            placeholder="Wybierz..."
            itemTemplate={userOptionTemplate}
            panelFooterTemplate={panelFooterTemplate}
            style={{ width: width ?? "100%" }}
            filter
            filterBy="searchField"
            className={className}
            loading={usersLoading || institutionsLoading}
            inputId="user-select"
            disabled={disabled}
          />
          <label htmlFor="user-select">{label}</label>
        </FloatLabel>
      )}
    </>

  );
}
